import React from 'react';
import ChatCard from '../ChatCard';
import { isOngoingBooking } from '../bookingStatusHelpers';
import '../ChatCard.css';
import './BookingListCards.css';

/**
 * Format duration_minutes into human-readable string.
 * e.g. 90 -> "1h 30m", 60 -> "1h", 30 -> "30m"
 */
const formatDuration = (minutes) => {
  if (!minutes || minutes <= 0) return null;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours > 0 && mins > 0) return `${hours}h ${mins}m`;
  if (hours > 0) return `${hours}h`;
  return `${mins}m`;
};

/**
 * Display booking list cards.
 * Uses shared ChatCard component for image + content layout.
 * Matches room selection card design: emoji via CSS ::before, simple <p> text.
 *
 * @param {Array} bookings - Booking objects from raw_output.bookings
 * @param {function} [onImageLoad] - Callback when an image loads (e.g. scrollToBottom)
 */
const BookingListCards = ({ bookings, onImageLoad }) => {
  if (!bookings || bookings.length === 0) return null;

  return (
    <div className="chat-cards-grid">
      {bookings.map((booking, index) => {
        const isCancelled = booking.status?.toLowerCase() === 'cancelled';
        const isOngoing = !isCancelled && isOngoingBooking(booking);
        const isConfirmed = !isOngoing
          && (booking.status?.toLowerCase() === 'confirmed' || booking.status?.toLowerCase() === 'active');
        const amenities = booking.amenity_titles || [];
        const duration = formatDuration(booking.duration_minutes);

        // Extract year from start_time ISO string (e.g. "2026-03-29T13:00:00-06:00")
        const year = booking.start_time ? new Date(booking.start_time).getFullYear() : null;

        // Build date string: "Mar 29, 2026" — skip appending when
        // date_converted already contains the year (backend sends
        // "Jul 11, 2026"; appending again showed "Jul 11, 2026, 2026")
        const dateStr = booking.date_converted
          ? booking.date_converted.includes(String(year))
            ? booking.date_converted
            : (year ? `${booking.date_converted}, ${year}` : booking.date_converted)
          : null;

        // Build time string: "01:00 PM – 03:30 PM MDT · 2h 30m"
        let timeStr = '';
        if (booking.start_time_converted && booking.end_time_converted) {
          timeStr = `${booking.start_time_converted} – ${booking.end_time_converted}`;
          if (duration) timeStr += ` · ${duration}`;
        }

        return (
          <ChatCard
            key={booking.reference || booking.id || `booking-${index}`}
            image={booking.resource_image_url}
            imageAlt={booking.resource_name || 'Booking'}
            onImageLoad={onImageLoad}
          >
            <div className={isCancelled ? 'booking-list-card-cancelled' : ''}>
              <h4 className="booking-list-title">
                {booking.resource_name}
                {isOngoing && (
                  <span className="booking-status-badge booking-status-ongoing">Ongoing</span>
                )}
                {isConfirmed && (
                  <span className="booking-status-badge booking-status-confirmed">Confirmed</span>
                )}
                {isCancelled && (
                  <span className="booking-status-badge booking-status-cancelled">Cancelled</span>
                )}
              </h4>

              {dateStr && (
                <p className="booking-list-date">{dateStr}</p>
              )}

              {timeStr && (
                <p className="booking-list-time">{timeStr}</p>
              )}

              {booking.resource_capacity && (
                <p className="booking-list-capacity">
                  Capacity: {booking.resource_capacity} {booking.resource_capacity === 1 ? 'person' : 'people'}
                </p>
              )}

              {booking.location_name && (
                <p className="booking-list-location">{booking.location_name}</p>
              )}

              {booking.reference && (
                <p className="booking-list-ref">
                  Ref: <span className="booking-list-reference">{booking.reference}</span>
                </p>
              )}

              {amenities.length > 0 && (
                <div className="booking-list-amenities">
                  {amenities.map((amenity) => (
                    <span key={amenity} className="booking-list-amenity-tag">{amenity}</span>
                  ))}
                </div>
              )}
            </div>
          </ChatCard>
        );
      })}
    </div>
  );
};

export default React.memo(BookingListCards);
